import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInboundAddress, regenerateInboundAddress, lockInboundAddress, getInboundLog, formatApiError } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ForwardingGuide } from "@/components/ForwardingGuide";
import { Mail, Copy, Check, RefreshCw, Loader2, CheckCircle2, ChevronDown, Send, ArrowUpRight, ClipboardCopy, Forward, ListChecks, Lock, Unlock } from "lucide-react";

const STATUS = {
  processed: { cls: "bg-emerald-500/15 text-emerald-500", label: "Added to Review Queue" },
  processing: { cls: "bg-sky-500/15 text-sky-500", label: "Reading…" },
  skipped: { cls: "bg-amber-500/15 text-amber-500", label: "Unknown sender — approve below" },
  failed: { cls: "bg-red-500/15 text-red-500", label: "Couldn't read" },
  empty: { cls: "bg-muted text-muted-foreground", label: "No invoice found" },
  verification: { cls: "bg-violet-500/15 text-violet-400", label: "Gmail confirmation" },
};

const VerificationRow = ({ e }) => {
  const [copied, setCopied] = useState(false);
  const copyCode = async () => {
    await navigator.clipboard.writeText(e.verification_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/5 px-3 py-2" data-testid="inbound-verification">
      <span className="text-xs text-muted-foreground">Gmail asks you to confirm forwarding.</span>
      {e.verification_code && (
        <button onClick={copyCode} className="flex items-center gap-1.5 rounded-md bg-background px-2 py-1 font-mono text-sm" data-testid="inbound-verification-code">
          {e.verification_code} {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>
      )}
      {e.verification_link && <a href={e.verification_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid="inbound-verification-link">Confirm with one click <ArrowUpRight className="inline h-3 w-3" /></a>}
      {!e.verification_code && !e.verification_link && <span className="text-xs text-amber-500">Couldn't find the code — check the Gmail email itself.</span>}
    </div>
  );
};

const HOW = [
  { icon: ClipboardCopy, title: "Copy the address", text: "It's unique to your workspace — treat it like a private inbox." },
  { icon: Forward, title: "Forward an invoice email", text: "By hand, or set a rule so your mailbox forwards them automatically." },
  { icon: ListChecks, title: "Find it in the Review Queue", text: "Within a minute D20 reads the PDF, codes it and flags anything unsure." },
];

export const ForwardingAddressCard = () => {
  const { isAdmin } = useApp();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [log, setLog] = useState({ items: [], total: 0 });
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    getInboundAddress().then(setInfo).catch(() => {});
    const poll = () => getInboundLog(20).then(setLog).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, []);

  if (!info) return null;
  const address = info.address || `${info.local_part}@your-domain`;

  const copy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success("Address copied — paste it into a forward or a mailbox rule");
    setTimeout(() => setCopied(false), 1500);
  };

  const regen = async () => {
    if (!window.confirm("Create a new address? The current one stops working immediately and any forwarding rules must be updated.")) return;
    setBusy(true);
    try {
      setInfo(await regenerateInboundAddress());
      toast.success("New address created");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Could not create a new address"));
    }
    setBusy(false);
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      setInfo(await lockInboundAddress(!info.locked));
      toast.success(info.locked ? "Address unlocked — it can be regenerated again" : "Address locked — it can't be regenerated until you unlock it");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-5" data-testid="forwarding-address-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-rose-500/10 text-rose-400"><Mail className="h-5 w-5" /></div>
          <div>
            <div className="font-semibold">Email invoices to D20</div>
            <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">Forward any invoice email to your D20 address and it's read, coded and queued automatically. Works from Outlook, Microsoft 365, Gmail or any other mailbox.</p>
          </div>
        </div>
        {info.ready ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-1 text-xs text-emerald-500" data-testid="forwarding-status"><CheckCircle2 className="h-3.5 w-3.5" /> Ready to receive</span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-1 text-xs text-amber-500" data-testid="forwarding-status">Being set up</span>
        )}
      </div>

      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Your D20 address</div>
        <div className="flex flex-wrap items-center gap-2">
          <code data-testid="forwarding-address" className={cn("text-base sm:text-lg font-mono break-all", !info.address && "text-muted-foreground")}>{address}</code>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid="forwarding-copy-btn" size="sm" onClick={copy}>{copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}{copied ? "Copied" : "Copy address"}</Button>
          {info.address && (
            <Button data-testid="forwarding-test-btn" size="sm" variant="outline" asChild>
              <a href={`mailto:${info.address}?subject=${encodeURIComponent("Test invoice for D20")}&body=${encodeURIComponent("Attach an invoice PDF to this email and send it. It will appear in D20 within a minute.")}`}><Send className="h-4 w-4 mr-1.5" /> Send a test email</a>
            </Button>
          )}
        </div>
      </div>

      {!info.ready && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-muted-foreground" data-testid="forwarding-setup-hint">
          This address isn't live yet — your D20 administrator is finishing the email setup. You can already copy it; it will keep working once live.
          {isAdmin && <div className="mt-1 text-xs">Admin: set <code>INBOUND_DOMAIN</code>, <code>RESEND_API_KEY</code> and <code>RESEND_WEBHOOK_SECRET</code>; webhook <code className="break-all">{info.webhook_url}</code> (event <code>email.received</code>).</div>}
        </div>
      )}

      <ol className="grid gap-3 sm:grid-cols-3" data-testid="forwarding-how-it-works">
        {HOW.map((h, i) => (
          <li key={h.title} className="rounded-lg border border-border bg-background/50 p-3">
            <div className="flex items-center gap-2 text-sm font-medium"><span className="font-mono text-xs text-primary">{i + 1}</span><h.icon className="h-4 w-4 text-muted-foreground" />{h.title}</div>
            <p className="mt-1 text-xs text-muted-foreground">{h.text}</p>
          </li>
        ))}
      </ol>

      <div>
        <button data-testid="forwarding-guide-toggle" onClick={() => setShowGuide((v) => !v)} className="flex items-center gap-1 text-sm text-primary hover:underline">
          <ChevronDown className={cn("h-4 w-4 transition-transform", showGuide && "rotate-180")} /> Set up automatic forwarding (optional, 2 minutes)
        </button>
        {showGuide && <div className="mt-3"><ForwardingGuide /></div>}
      </div>

      <p className="text-xs text-muted-foreground" data-testid="forwarding-sender-note">Emails from your team and Trusted Senders are read straight away. Anyone else waits in <b>Skipped senders</b> below until you approve them, so strangers can't fill your queue.</p>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Recently received <span className="ml-1 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground" data-testid="inbound-log-count">{log.total}</span></div>
          {isAdmin && (
            <div className="flex items-center gap-3">
              <button data-testid="forwarding-lock-btn" onClick={toggleLock} disabled={busy}
                      className={cn("flex items-center gap-1 text-xs", info.locked ? "text-emerald-500 hover:text-emerald-400" : "text-muted-foreground hover:text-foreground")}
                      title={info.locked ? "Unlock to allow a new address" : "Lock so nobody regenerates the address by accident"}>
                {info.locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />} {info.locked ? "Locked · Unlock" : "Lock address"}
              </button>
              <button data-testid="forwarding-regenerate-btn" onClick={regen} disabled={busy || info.locked}
                      title={info.locked ? "Unlock the address first" : "Create a new address (the current one stops working)"}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} New address
              </button>
            </div>
          )}
        </div>
        {log.items.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="inbound-log-empty">Nothing yet. Forward an invoice email to your address and it will show up here.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="inbound-log">
            {log.items.map((e) => {
              const st = STATUS[e.status] || STATUS.processing;
              return (
                <li key={e.email_id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3" data-testid={`inbound-row-${e.email_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{e.subject || "(no subject)"}</div>
                    <div className="text-xs text-muted-foreground truncate">From {e.from_email || e.from} · {new Date(e.received_at).toLocaleString()}{e.attachments?.length ? ` · ${e.attachments.length} attachment${e.attachments.length > 1 ? "s" : ""}` : ""}</div>
                    {e.error && e.status === "failed" && <div className="text-xs text-amber-500 mt-0.5">{e.error}</div>}
                    {e.status === "verification" && <VerificationRow e={e} />}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {e.created?.slice(0, 2).map((c) => (
                      <button key={c.id} onClick={() => navigate(c.kind === "statement" ? "/suppliers" : `/invoice/${c.id}`)} className="text-xs text-primary hover:underline flex items-center gap-0.5" data-testid="inbound-created-link">
                        {c.supplier} {c.invoice_number} <ArrowUpRight className="h-3 w-3" />
                      </button>
                    ))}
                    <span className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-medium", st.cls)} data-testid="inbound-status">{st.label}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
